import { useState, useEffect, useRef, useCallback } from "react";
import { QRCodeSVG } from "qrcode.react";
import { api } from "../../lib/api";
import {
  Share2,
  Copy,
  Check,
  Mail,
  MessageSquare,
  Download,
  X,
  Trash2,
  Loader2,
  Calendar,
  Eye,
  Image as ImageIcon,
} from "lucide-react";
import { toast } from "sonner";

/**
 * Iter Y — Client Progress Portal share dialog.
 *
 * Mounted from session-finished surfaces. Lets the therapist:
 *   1. Create a new portal link with options (TTL, include photos, message)
 *   2. See an at-a-glance QR + copyable URL + Open Portal preview
 *   3. List + revoke existing links for this session
 *   4. Quick-share via sms / mailto / native share where available
 *
 * Props:
 *   sessionId      required
 *   open           controlled open state
 *   onClose
 */
export default function PortalShareDialog({ sessionId, open, onClose }) {
  const [links, setLinks] = useState([]);
  const [loadingLinks, setLoadingLinks] = useState(false);
  const [creating, setCreating] = useState(false);
  const [includePhotos, setIncludePhotos] = useState(false);
  const [ttl, setTtl] = useState(30);
  const [message, setMessage] = useState("");
  const [copiedToken, setCopiedToken] = useState(null);
  const dialogRef = useRef(null);

  const loadLinks = useCallback(async () => {
    if (!sessionId) return;
    setLoadingLinks(true);
    try {
      const r = await api.get(`/portal/sessions/${sessionId}/links`);
      setLinks(r.data || []);
    } catch {
      // ignore
    } finally {
      setLoadingLinks(false);
    }
  }, [sessionId]);

  useEffect(() => { if (open) loadLinks(); }, [open, loadLinks]);

  useEffect(() => {
    if (!open) return;
    const onKey = (e) => { if (e.key === "Escape") onClose?.(); };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [open, onClose]);

  if (!open) return null;

  const portalBaseUrl = (() => {
    if (typeof window === "undefined") return "";
    return `${window.location.origin}/p/`;
  })();

  const create = async () => {
    setCreating(true);
    try {
      const r = await api.post(`/portal/sessions/${sessionId}`, {
        ttl_days: ttl,
        include_photos: includePhotos,
        therapist_message: message || null,
      });
      toast.success("Shareable link created");
      setMessage("");
      setLinks((cur) => [r.data, ...cur]);
    } catch (e) {
      toast.error("Could not create link. " + (e?.response?.data?.detail || ""));
    } finally {
      setCreating(false);
    }
  };

  const revoke = async (token) => {
    if (!window.confirm("Revoke this link? The client will no longer be able to open it.")) return;
    try {
      await api.delete(`/portal/links/${token}`);
      setLinks((cur) => cur.map((l) => l.token === token ? { ...l, revoked_at: new Date().toISOString() } : l));
      toast.success("Link revoked");
    } catch {
      toast.error("Could not revoke link.");
    }
  };

  const copy = async (token) => {
    const url = portalBaseUrl + token;
    let ok = false;
    try {
      if (navigator.clipboard && window.isSecureContext) {
        await navigator.clipboard.writeText(url);
        ok = true;
      }
    } catch {
      ok = false;
    }
    if (!ok) {
      // Fallback: synthesize a textarea + execCommand('copy') (works without user-gesture in test browsers)
      try {
        const ta = document.createElement("textarea");
        ta.value = url;
        ta.setAttribute("readonly", "");
        ta.style.position = "fixed";
        ta.style.opacity = "0";
        document.body.appendChild(ta);
        ta.select();
        ta.setSelectionRange(0, ta.value.length);
        document.execCommand("copy");
        document.body.removeChild(ta);
        ok = true;
      } catch {
        ok = false;
      }
    }
    setCopiedToken(token);
    setTimeout(() => setCopiedToken((t) => t === token ? null : t), 1500);
    if (ok) {
      toast.success("Link copied — ready to paste");
    } else {
      toast.message("Tap the URL above and copy it manually.");
    }
  };

  const downloadQr = (token) => {
    const svg = document.getElementById(`qr-${token}`);
    if (!svg) return;
    const xml = new XMLSerializer().serializeToString(svg);
    const blob = new Blob([xml], { type: "image/svg+xml" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `posturra-portal-${token.slice(0, 8)}.svg`;
    a.click();
    setTimeout(() => URL.revokeObjectURL(url), 500);
  };

  const activeLinks = links.filter((l) => !l.revoked_at);

  return (
    <div
      className="fixed inset-0 z-50 flex items-end sm:items-center justify-center p-0 sm:p-4"
      role="dialog"
      aria-modal="true"
      data-testid="portal-share-dialog"
    >
      <button
        type="button"
        aria-label="Close"
        onClick={onClose}
        className="absolute inset-0 bg-ink-900/40 backdrop-blur-sm"
        data-testid="portal-dialog-backdrop"
      />
      <div ref={dialogRef} className="relative w-full sm:max-w-[640px] max-h-[92vh] overflow-y-auto bg-white sm:rounded-3xl rounded-t-3xl shadow-2xl">
        <div className="sticky top-0 bg-white border-b border-paper-100 px-6 py-5 flex items-start justify-between gap-3 z-10">
          <div>
            <div className="label-overline text-sage-600 mb-1 flex items-center gap-1.5">
              <Share2 className="w-3.5 h-3.5" /> Share with client
            </div>
            <h2 className="font-heading text-2xl text-ink-900">Progress portal</h2>
            <p className="text-xs text-ink-500 mt-1">A secure, expiring link the client can open from a text or email — no login required.</p>
          </div>
          <button
            type="button"
            onClick={onClose}
            data-testid="portal-dialog-close"
            className="w-9 h-9 rounded-full hover:bg-paper-100 inline-flex items-center justify-center"
            aria-label="Close"
          >
            <X className="w-4 h-4" />
          </button>
        </div>

        <div className="p-6 space-y-6">
          {/* Create new link */}
          <div className="bg-paper-50 border border-paper-200 rounded-2xl p-4 space-y-3" data-testid="portal-create-section">
            <div className="label-overline text-ink-700">New link</div>
            <div>
              <label className="text-xs text-ink-500 block mb-1">Optional message (shown at the top of the portal)</label>
              <textarea
                value={message}
                onChange={(e) => setMessage(e.target.value)}
                rows={2}
                placeholder='"Great session today, Sarah — here\u2019s a quick recap and your home care."'
                data-testid="portal-message-input"
                className="w-full px-3 py-2 rounded-xl bg-white border border-paper-200 outline-none text-sm focus:ring-2 focus:ring-sage-500/30 focus:border-sage-500"
              />
            </div>
            <div className="flex items-center gap-4 flex-wrap">
              <label className="text-xs text-ink-700 inline-flex items-center gap-2" data-testid="portal-ttl-row">
                Link expires in
                <select
                  value={ttl}
                  onChange={(e) => setTtl(parseInt(e.target.value))}
                  data-testid="portal-ttl-select"
                  className="min-h-[36px] px-2 rounded-xl bg-white border border-paper-200 text-sm"
                >
                  <option value="7">7 days</option>
                  <option value="14">14 days</option>
                  <option value="30">30 days</option>
                  <option value="60">60 days</option>
                  <option value="90">90 days</option>
                </select>
              </label>
              <label className="text-xs text-ink-700 inline-flex items-center gap-2 cursor-pointer">
                <input
                  type="checkbox"
                  checked={includePhotos}
                  onChange={(e) => setIncludePhotos(e.target.checked)}
                  data-testid="portal-include-photos"
                  className="w-4 h-4 accent-sage-500"
                />
                <ImageIcon className="w-3.5 h-3.5" /> Include posture photos
              </label>
              <div className="flex-1" />
              <button
                type="button"
                onClick={create}
                disabled={creating}
                data-testid="portal-create-btn"
                className="min-h-[40px] px-5 rounded-full bg-sage-500 hover:bg-sage-600 text-white text-sm font-medium inline-flex items-center gap-2 disabled:opacity-60"
              >
                {creating ? <Loader2 className="w-4 h-4 animate-spin" /> : <Share2 className="w-4 h-4" />}
                {creating ? "Creating…" : "Generate link"}
              </button>
            </div>
          </div>

          {/* Existing links */}
          <div data-testid="portal-existing-section">
            <div className="label-overline mb-3 text-ink-700">Active links</div>
            {loadingLinks && (
              <div className="text-sm text-ink-500">Loading…</div>
            )}
            {!loadingLinks && activeLinks.length === 0 && (
              <div className="text-sm text-ink-500" data-testid="portal-no-active-links">
                No active links yet — create one above.
              </div>
            )}
            {!loadingLinks && activeLinks.map((l) => {
              const url = portalBaseUrl + l.token;
              const isCopied = copiedToken === l.token;
              return (
                <PortalLinkCard
                  key={l.token}
                  link={l}
                  url={url}
                  isCopied={isCopied}
                  onCopy={() => copy(l.token)}
                  onRevoke={() => revoke(l.token)}
                  onDownloadQr={() => downloadQr(l.token)}
                />
              );
            })}
          </div>
        </div>
      </div>
    </div>
  );
}

function PortalLinkCard({ link, url, isCopied, onCopy, onRevoke, onDownloadQr }) {
  const expires = new Date(link.expires_at);
  const daysLeft = Math.max(0, Math.ceil((expires.getTime() - Date.now()) / (1000 * 60 * 60 * 24)));
  const smsHref = `sms:?&body=${encodeURIComponent("Here's your session recap: " + url)}`;
  const emailHref = `mailto:?subject=${encodeURIComponent("Your session recap")}&body=${encodeURIComponent("Hi — here's a quick summary of today's session:\n\n" + url)}`;

  return (
    <div className="bg-white border border-paper-200 rounded-2xl p-4 mt-3" data-testid={`portal-link-${link.token}`}>
      <div className="flex items-start gap-4 flex-wrap">
        <div className="bg-white border border-paper-200 rounded-2xl p-2.5 shrink-0">
          <QRCodeSVG
            id={`qr-${link.token}`}
            value={url}
            size={96}
            bgColor="#FFFFFF"
            fgColor="#3F3A33"
            level="M"
          />
        </div>
        <div className="flex-1 min-w-[200px]">
          <div className="flex items-center gap-1.5 text-xs text-ink-500 mb-1">
            <Calendar className="w-3 h-3" /> expires in {daysLeft} day{daysLeft === 1 ? "" : "s"}
            <span className="mx-1">·</span>
            <Eye className="w-3 h-3" /> {link.view_count || 0} view{link.view_count === 1 ? "" : "s"}
          </div>
          <div className="text-xs font-mono text-ink-900 break-all bg-paper-50 border border-paper-100 rounded-lg px-2 py-1.5 mb-2 select-all" data-testid={`portal-url-${link.token}`}>
            {url}
          </div>
          {link.therapist_message && (
            <div className="text-xs text-ink-500 italic mb-2 line-clamp-2">"{link.therapist_message}"</div>
          )}
          <div className="flex flex-wrap gap-1.5">
            <button
              type="button"
              onClick={onCopy}
              data-testid={`portal-copy-${link.token}`}
              className="min-h-[36px] px-3 rounded-full text-xs bg-ink-900 hover:bg-ink-700 text-white inline-flex items-center gap-1.5"
            >
              {isCopied ? <Check className="w-3.5 h-3.5" /> : <Copy className="w-3.5 h-3.5" />}
              {isCopied ? "Copied" : "Copy link"}
            </button>
            <a
              href={smsHref}
              data-testid={`portal-sms-${link.token}`}
              className="min-h-[36px] px-3 rounded-full text-xs border border-paper-200 text-ink-700 hover:bg-paper-50 inline-flex items-center gap-1.5"
            >
              <MessageSquare className="w-3.5 h-3.5" /> Text
            </a>
            <a
              href={emailHref}
              data-testid={`portal-email-${link.token}`}
              className="min-h-[36px] px-3 rounded-full text-xs border border-paper-200 text-ink-700 hover:bg-paper-50 inline-flex items-center gap-1.5"
            >
              <Mail className="w-3.5 h-3.5" /> Email
            </a>
            <a
              href={url}
              target="_blank"
              rel="noopener noreferrer"
              data-testid={`portal-preview-${link.token}`}
              className="min-h-[36px] px-3 rounded-full text-xs border border-paper-200 text-ink-700 hover:bg-paper-50 inline-flex items-center gap-1.5"
            >
              Preview
            </a>
            <button
              type="button"
              onClick={onDownloadQr}
              data-testid={`portal-qr-download-${link.token}`}
              className="min-h-[36px] px-3 rounded-full text-xs border border-paper-200 text-ink-700 hover:bg-paper-50 inline-flex items-center gap-1.5"
            >
              <Download className="w-3.5 h-3.5" /> QR
            </button>
            <button
              type="button"
              onClick={onRevoke}
              data-testid={`portal-revoke-${link.token}`}
              className="min-h-[36px] px-3 rounded-full text-xs text-terracotta-600 hover:bg-terracotta-500/10 inline-flex items-center gap-1.5"
            >
              <Trash2 className="w-3.5 h-3.5" /> Revoke
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
